import { merge } from '@fluentui/react';
import { useAnalytics } from '@msrceng/analytics';
import axios from 'axios';
import { map } from 'lodash';
import { useMutation, useQueryClient, } from '@tanstack/react-query';
/**
 * A string:string enum of HTTP verbs that go from client to server and include data
 * (put, patch, post, create)
 */
export var HTTPMutationVerb;
(function (HTTPMutationVerb) {
    HTTPMutationVerb["PUT"] = "PUT";
    HTTPMutationVerb["PATCH"] = "PATCH";
    HTTPMutationVerb["POST"] = "POST";
    HTTPMutationVerb["CREATE"] = "CREATE";
})(HTTPMutationVerb || (HTTPMutationVerb = {}));
/** Options if the user configures none */
const defaultOptions = {
    delayAdditionalEndpoints: 20,
    useMutationOptions: {},
};
/**
 * Premade OData API for mutation (sending data to the server).
 *
 * > This has 2 different mutation signatures depending on how your endpoint takes data.
 * > - `/endpoint/` with a payload of TYPE which may or may not include idOfType (depends on API)
 * > - `/endpoint/idOfType` with a payload of `Omit<TYPE,'idOfType'>`
 *
 * @example // For `/endpoint/` (probably use this for create mutations that use PUT/POST)
 * const { mutate } = useOdataPostSingleApi<MyType>(endpoint)
 * //elsewhere
 * // This will create a POST to "/endpoint/" with a payload of MyType
 * mutate({
 *   idOfType: '1',
 *   thing1: 1,
 *   thing2: 2
 * })
 * @example // For `/endpoint/idOfType` (probably use this for PATCH requests that change an existing entity)
 * const { mutate } = useOdataPostSingleApi<Omit<MyType,'idOfType'>>(endpoint, [], HTTPMutationVerb.PATCH);
 * // Alternatively if you're trying to send a partial delta (IE changing one val on a profile):
 * // const { mutate } =
 * //    useOdataPostSingleApi<Partial<Omit<MyType,'idOfType'>>>(endpoint, [], HTTPMutationVerb.PATCH);
 *
 * //elsewhere
 * // This will create a PATCH to "/endpoint/1" with a payload of MyType without its "idOfType"
 * mutate({
 *   itemId: '1',
 *   payload:{
 *     thing1: 1,
 *     thing2: 2
 *   }
 * })
 */
export const useOdataPostSingleApi = (
/** The url path (relative) of this entity on top of the apiRoot */
endpointName, additionalInvalidateEndpoints, httpVerb = HTTPMutationVerb.POST, options) => {
    const { appInsights } = useAnalytics();
    const { delayAdditionalEndpoints, useMutationOptions } = merge({}, defaultOptions, options);
    const queryClient = useQueryClient();
    return useMutation((variables) => {
        const isItemIdAndPayload = variables.type === 'ITEM_AND_PAYLOAD';
        const finalEndpoint = isItemIdAndPayload
            ? endpointName + variables.itemId
            : endpointName;
        const finalContent = isItemIdAndPayload
            ? variables.payload
            : variables;
        const req = axios[httpVerb.toLowerCase()](finalEndpoint, finalContent);
        return req.then(res => res.data);
    }, Object.assign(Object.assign({}, useMutationOptions), { mutationKey: [endpointName], onError: error => appInsights === null || appInsights === void 0 ? void 0 : appInsights.trackException({
            exception: error,
        }), onSuccess: () => {
            //Slightly delay invalidation so the backend has time for processing
            //and we don't accidentally refetch stale data
            setTimeout(() => {
                //get rid of the query key
                queryClient.invalidateQueries([endpointName], {
                    type: 'inactive',
                    refetchType: 'all',
                });
                map(additionalInvalidateEndpoints, e => {
                    queryClient.invalidateQueries([e], {
                        type: 'inactive',
                        refetchType: 'all',
                        exact: false,
                    });
                });
            }, delayAdditionalEndpoints);
        } }));
};
